// src/components/NodeItem/AddNodeForm.jsx
import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  createThreadWithFirstNode,
  addCategory,
  getCategories,
} from "../../modules/firebaseAPI";
import bcrypt from "bcryptjs"; // ← 追加（npm install bcryptjs）

const AddNodeForm = ({ userId, initialData = {} }) => {
  const navigate = useNavigate();

  // ────────── 再開モード対応 ──────────
  const [title, setTitle] = useState(initialData.title || "");
  const [content, setContent] = useState(initialData.previousContent || "");
  const [categoryInput, setCategoryInput] = useState(
    initialData.category || ""
  );
  const [parentId] = useState(initialData.parentId || null);
  const [baseStoryId] = useState(initialData.storyId || null);
  const [allCategories, setAllCategories] = useState([]);
  const [threadType, setThreadType] = useState(
    initialData.threadType || "story"
  );
  const [voteMethod, setVoteMethod] = useState(
    initialData.voteMethod || "author"
  );
  const [voteTime, setVoteTime] = useState(initialData.voteTime || 3);
  const [showCategoryList, setShowCategoryList] = useState(false);
  const categoryRef = useRef(null);
  const formRef = useRef(null);
  const [firstNodeId] = useState(initialData.firstNodeId || null);
  const [parentNodeId] = useState(initialData.parentNodeId || parentId);

  const [password, setPassword] = useState("");
  const [enablePassword, setEnablePassword] = useState(false);

  // ────────── カテゴリ取得 ──────────
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const categories = await getCategories();
        setAllCategories(categories);
      } catch (err) {
        console.error("カテゴリ取得失敗:", err);
      }
    };
    fetchCategories();
  }, []);

  // ────────── 外クリックでカテゴリリスト閉じる ──────────
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (formRef.current && !formRef.current.contains(event.target)) {
        setShowCategoryList(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // ────────── ESCキーでカテゴリリスト閉じる ──────────
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape") setShowCategoryList(false);
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, []);

  // ────────── カテゴリサジェスト外クリックでカテゴリリスト閉じる ──────────
  const containerRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target)
      ) {
        setShowCategoryList(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // ────────── カテゴリ候補絞り込み ──────────
  const filteredCategories = allCategories.filter((cat) =>
    cat.toLowerCase().includes(categoryInput.toLowerCase())
  );
  const handleCategorySelect = (cat) => {
    setCategoryInput(cat);
    setShowCategoryList(false);
  };

  // ────────── フォーム送信 ──────────
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!title || !content.trim()) {
      alert("タイトルと開始文は必須です。");
      return;
    }

    try {
      // 新規カテゴリ追加
      if (categoryInput && !allCategories.includes(categoryInput)) {
        await addCategory(categoryInput);
        setAllCategories((prev) => [...prev, categoryInput]);
      }

      // 🔒 パスワード処理
      let hashedPassword = null;

      if (initialData.password) {
        // 再開モード：初期データのハッシュを使用
        hashedPassword = initialData.password;
      } else if (password && password.trim() !== "") {
        // 新規作成モード：入力パスワードをハッシュ化
        const cleanPassword = password.trim();

        const salt = bcrypt.genSaltSync(10);
        hashedPassword = bcrypt.hashSync(cleanPassword, salt);
      } else {
        //console.log("パスワード未設定または空白");
      }

      // Firestoreに送信するデータ確認
      const firestoreData = {
        title,
        content,
        category: categoryInput || "その他",
        threadType,
        voteMethod,
        voteTime,
        createdBy: userId,
        baseStoryId: baseStoryId || undefined,
        parentNodeId: parentNodeId || undefined,
        firstNodeId: firstNodeId || undefined,
        password: hashedPassword,
      };
      //console.log("Firestoreに送信するデータ:", firestoreData);

      // Firestoreへ送信
      const { storyId: newStoryId, firstNodeId: createdFirstNodeId } =
        await createThreadWithFirstNode(firestoreData);

      /*
      console.log("Firestore送信完了: 新規ストーリーID:", newStoryId);
      console.log(
        "Firestore送信完了: 作成された最初のノードID:",
        createdFirstNodeId
      );*/

      // 入力リセット
      setTitle("");
      setContent("");
      setCategoryInput("");
      setThreadType("story");
      setVoteMethod("author");
      setVoteTime(3);
      setPassword("");

      // 作成完了後に遷移
      navigate(`/chat/${createdFirstNodeId}`, {
        state: {
          storyId: newStoryId,
          nodeId: createdFirstNodeId,
          title,
          content,
          category: categoryInput,
          threadType,
          voteMethod,
          previousStoryId: baseStoryId,
          parentId,
        },
      });
    } catch (error) {
      //console.error("スレッド作成失敗:", error);
      alert("スレッドの作成に失敗しました。");
    }
  };

  return (
    <form
      ref={formRef}
      onSubmit={handleSubmit}
      style={{ position: "relative" }}
    >
      {/* 再開モード */}
      {baseStoryId && (
        <p
          style={{ background: "#f0f0f0", padding: "6px", borderRadius: "6px" }}
        >
          🔁 再開モード: 元のストーリー <strong>{baseStoryId}</strong> から作成
        </p>
      )}
      <br />

      {/* スレッドタイトル */}
      <div style={{ marginBottom: "10px" }}>
        <label>
          スレッドタイトル <span style={{ color: "#888" }}>(必須)</span>:
          <br />
          <input
            type="text"
            placeholder="スレッドのタイトルを入力してください"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            required
            style={{ width: "90%", padding: "6px", marginTop: "4px" }}
          />
        </label>
      </div>

      {/* 開始文 */}
      <div style={{ marginBottom: "10px" }}>
        <label>
          開始文 <span style={{ color: "#888" }}>(必須)</span>:
          <br />
          <textarea
            placeholder="このスレッドの最初の文章を入力してください"
            value={content}
            onChange={(e) => setContent(e.target.value)}
            required
            style={{ width: "90%", padding: "6px", marginTop: "4px" }}
          />
        </label>
      </div>

      {/* カテゴリ */}
      <div
        ref={containerRef}
        style={{ position: "relative", marginBottom: "10px" }}
      >
        <label>
          カテゴリ <span style={{ color: "#888" }}>(任意)</span>:
          <br />
          <input
            ref={categoryRef}
            type="text"
            placeholder="カテゴリを入力（例: 旅行、学習など）"
            value={categoryInput}
            onChange={(e) => {
              setCategoryInput(e.target.value);
              setShowCategoryList(true);
            }}
            onFocus={() => setShowCategoryList(true)}
            style={{ width: "90%", padding: "6px", marginTop: "4px" }}
          />
        </label>
        {showCategoryList && filteredCategories.length > 0 && (
          <ul
            style={{
              position: "absolute",
              zIndex: 10,
              background: "white",
              border: "1px solid #ccc",
              width: "90%",
              maxHeight: "150px",
              overflowY: "auto",
              padding: 0,
              margin: 0,
              listStyle: "none",
            }}
          >
            {filteredCategories.map((cat) => (
              <li
                key={cat}
                onClick={() => handleCategorySelect(cat)}
                style={{ padding: "5px 10px", cursor: "pointer" }}
                onMouseDown={(e) => e.preventDefault()}
              >
                {cat}
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* スレッドタイプ */}
      <div style={{ marginBottom: "10px" }}>
        <label>
          スレッドタイプ:
          <select
            value={threadType}
            onChange={(e) => setThreadType(e.target.value)}
            style={{ marginLeft: "8px" }}
          >
            <option value="story">ストーリー型</option>
            <option value="itinerary">マインドマップ型</option>
          </select>
        </label>
      </div>

      {/* 投票方法 */}
      <div style={{ marginBottom: "10px" }}>
        <label>
          投票方法:
          <select
            value={voteMethod}
            onChange={(e) => setVoteMethod(e.target.value)}
            style={{ marginLeft: "8px" }}
          >
            <option value="author">作成者決定</option>
            <option value="vote">投票</option>
            <option value="timed">時間依存</option>
          </select>
        </label>
      </div>

      {/* 投票時間 */}
      {voteMethod !== "author" && (
        <div style={{ marginBottom: "10px" }}>
          <label>
            投票時間（分）:
            <input
              type="number"
              min="1"
              value={voteTime}
              onChange={(e) => setVoteTime(Number(e.target.value))}
              style={{ marginLeft: "8px", width: "60px", padding: "4px" }}
            />
            <span style={{ color: "#888", marginLeft: "6px" }}>※ 1分以上</span>
          </label>
        </div>
      )}

      {/* パスワード設定 */}
      <div style={{ marginBottom: "10px" }}>
        <label style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <input
            type="checkbox"
            checked={enablePassword}
            onChange={(e) => setEnablePassword(e.target.checked)}
          />
          パスワードを設定する <span style={{ color: "#888" }}></span>
        </label>
        {enablePassword && (
          <div style={{ marginTop: "6px" }}>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="パスワードを入力"
              style={{
                width: "90%",
                padding: "6px",
                border: "1px solid #ccc",
                borderRadius: "4px",
              }}
            />
          </div>
        )}
      </div>

      <button type="submit" style={{ padding: "8px 16px" }}>
        {baseStoryId ? "再開スレッドを開始" : "スレッドを開始"}
      </button>
    </form>
  );
};

export default AddNodeForm;
